import React from "react";
import {useTranslation} from "react-i18next";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import {makeLink} from "../common/languages-helper";
import {EXTERNAL_LINKS, SOCIAL_LINKS} from "../../config/external-links";
import {openConsentSettings} from "../../analytics/consent";
import "./Footer.css";

// Site footer, mirroring the Figma "EMDR Online" footer frame: a brand/company
// block on the left, link columns, stacked social buttons on the right, and a
// bottom bar.
//
// TODO(team): supply real destinations for the "#" links and confirm the
// company contact block — the address ("45-32 Warszawa Turkusowa 43") and phone
// ("+48 555 555 555") come straight from the Figma placeholder data.
const Footer: React.FC = () => {
    const {t} = useTranslation();
    const year = new Date().getFullYear();

    const columns = [
        {
            title: t("Products and services"),
            links: [
                {label: t("EMDRlink Device"), url: EXTERNAL_LINKS.device},
                {label: t("Program EMDRlink On-line"), url: EXTERNAL_LINKS.about},
                {label: t("Find EMDR Therapist"), url: EXTERNAL_LINKS.findTherapist},
                {label: t("Online Training"), url: EXTERNAL_LINKS.training},
            ],
        },
        {
            title: t("Contact"),
            links: [
                {label: t("Contact us"), url: EXTERNAL_LINKS.contact},
            ],
        },
        {
            title: t("Shop"),
            links: [
                {label: t("Shop"), url: EXTERNAL_LINKS.shop},
            ],
        },
        {
            title: t("Technical support"),
            links: [
                {label: t("EMDR Online program manual"), url: EXTERNAL_LINKS.programManual},
                {label: t("Device user manual"), url: EXTERNAL_LINKS.deviceManual},
            ],
        },
    ];

    const isInternal = (url: string) => url.startsWith("/");
    const externalProps = (url: string) =>
        !isInternal(url) && url !== "#"
            ? {target: "_blank", rel: "noopener noreferrer"}
            : {};

    return (
        <footer className="site-footer">
            <div className="footer-inner">
                <div className="footer-top">
                    <div className="footer-brand">
                        <img className="footer-logo" src="/logos/emdr-logo-new.svg" alt="EMDRlink" />
                        <div className="footer-company-name">EMDRLink Sp. z o.o.</div>
                        <address className="footer-address">
                            Lipowa 45A<br />
                            05-800 Pruszków
                        </address>
                        <div className="footer-contact">
                            <span>+48 880 880 147</span>
                            <a href="mailto:emdrlink@emdrlink.pl">emdrlink@emdrlink.pl</a>
                        </div>
                    </div>

                    <div className="footer-columns">
                        {columns.map((col, i) => (
                            <div key={i} className="footer-col">
                                <h4 className="footer-col-title">{col.title}</h4>
                                <ul className="footer-links">
                                    {col.links.map((link, j) => (
                                        <li key={j}>
                                            <a href={link.url} {...externalProps(link.url)}>{link.label}</a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="footer-social">
                        <a className="footer-social-btn" href={SOCIAL_LINKS.facebook} aria-label="Facebook">
                            <FacebookIcon className="footer-social-icon fb" />
                            <span>Facebook</span>
                        </a>
                        <a className="footer-social-btn" href={SOCIAL_LINKS.instagram} aria-label="Instagram">
                            <InstagramIcon className="footer-social-icon ig" />
                            <span>Instagram</span>
                        </a>
                    </div>
                </div>

                <div className="footer-bottom">
                    <span className="footer-copy">© emdrlink 2021–{year}</span>
                    <button type="button" className="footer-cookie-settings" onClick={openConsentSettings}>
                        {t("Cookie settings")}
                    </button>
                    <span className="footer-report">
                        {t("Problems with the site?")}{" "}
                        <a href="mailto:support@emdrlink.pl">{t("Send a technical report")}</a>
                    </span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
